<template>
  <nav
    ref="root"
    class="desktop-navi"
    :class="{
      'desktop-navi--sticky': isSticky,
      'desktop-navi--animated': isAnimated,
      'desktop-navi--scroll-up': isScrollUp,
    }"
  >
    <max-width-container class="desktop-navi__container">
      <div class="desktop-navi__upper">
        <Logo class="desktop-navi__logo" :logo-href="logoHref"></Logo>
        <option-picker
          v-if="mainLinks"
          class="desktop-navi__main"
          :options="mainLinks"
        />
        <nav-link-list
          class="desktop-navi__main_navigation"
          view-type="label"
          data-track-as="navi.general"
          :links="[...generalLinks, ...specialLinks]"
        />
        <meta-navi
          class="desktop-navi__meta_navigation"
          :login-links="loginLinks"
          :is-reduced="isTablet"
          :secondary-links="secondaryLinks"
        />
        <option-picker
          v-show="languages.length > 0"
          class="desktop-navi__language"
          :options="languages"
          uppercase-titles
        />
      </div>
      <div class="desktop-navi__lower">
        <div v-if="level2Links.length" class="desktop-navi__secondary">
          <MainNavi
            class="desktop-navi__secondary_navigation"
            data-track-as="navi.secondary"
            :links="level2Links"
            :cta="level2CallToAction"
          />
        </div>
        <Leads :show-basket="showBasket" />
      </div>
    </max-width-container>
  </nav>
</template>

<script>
/**
 * TODO: Discuss the following
 * The navigation is broken into small components, but those cant be used outside of the navigation.
 * For instance the flex order & the height is inside the navigation level components but
 * the positioning is in the parent navigation
 **/
import { onBeforeUnmount, onMounted, ref } from '@vue/composition-api'

import Leads from '../common/Leads'
import Logo from '../common/Logo'
import MaxWidthContainer from '../common/MaxWidthContainer'
import MetaNavi from '../common/MetaNavi'
import NavLinkList from '../common/NavLinkList'
import { useNavigationTypeFilter } from '../composables/useNavigationTypeFilter'
import MainNavi from '../mainNavi/MainNavi'
import OptionPicker from './OptionPicker'

export default {
  name: 'DesktopNavi',
  components: {
    NavLinkList,
    Leads,
    MetaNavi,
    MainNavi,
    Logo,
    MaxWidthContainer,
    OptionPicker,
  },

  props: {
    showBasket: Boolean,

    level1Links: {
      type: Array,
      required: true,
    },

    level2Links: {
      type: Array,
      default: () => [],
    },

    level2CallToAction: {
      type: Object,
      default: () => null,
    },

    logoHref: {
      type: String,
      default: 'https://sanitas.com',
    },

    loginLinks: {
      type: Object,
      default: () => null,
    },

    secondaryLinks: {
      type: Array,
      default: () => [],
    },

    languages: {
      type: Array,
      default: () => [],
    },

    search: {
      type: Object,
      default: () => {
        return {
          label: undefined,
          url: undefined,
        }
      },
    },
  },
  setup(props) {
    const { generalLinks, specialLinks, mainLinks } =
      useNavigationTypeFilter(props)

    const root = ref(null)

    let lastScrollPos = 0
    let isSticky = ref(false)
    let isScrollUp = ref(false)
    let isAnimated = ref(false)
    let navHeight = 0
    let navHeightReduced = 0
    let lastAppliedRegion = undefined

    const REGIONS = {
      TOP: 'top',
      HEADER: 'header',
      BODY: 'body',
    }

    onMounted(() => {
      navHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--navigation-height',
        ),
        10,
      )
      navHeightReduced = parseInt(
        getComputedStyle(root.value).getPropertyValue(
          '--dotcom-desktop-navi-reduced-height',
        ),
        10,
      )
    })

    function scrollReset() {
      if (lastAppliedRegion === REGIONS.TOP) return
      isSticky.value = false
      isAnimated.value = false
      lastAppliedRegion = REGIONS.TOP
    }

    function scrollInBody() {
      if (lastAppliedRegion === REGIONS.BODY) return

      // Code below needs to be done only once for this region
      root.value.style.setProperty('--dotcom-desktop-navi-scroll-opacity', 0)
      root.value.style.setProperty('--dotcom-desktop-navi-scroll-scale', 0.5)
      root.value.style.setProperty(
        '--dotcom-desktop-navi-scroll-height',
        `${navHeight - navHeightReduced}px`,
      )
      isSticky.value = true
      /* Without the timeout the sticky navigation would be animated upwards at switch over from REGIONS.HEADER to REGIONS.BODY */
      setTimeout(() => {
        isAnimated.value = true
      }, 0)

      lastAppliedRegion = REGIONS.BODY
    }

    function scrollInHeader() {
      root.value.style.setProperty(
        '--dotcom-desktop-navi-scroll-opacity',
        1 - lastScrollPos / navHeight,
      )
      root.value.style.setProperty(
        '--dotcom-desktop-navi-scroll-scale',
        1 - (0.5 * lastScrollPos) / navHeight,
      )
      root.value.style.setProperty(
        '--dotcom-desktop-navi-scroll-height',
        `${((navHeight - navHeightReduced) * lastScrollPos) / navHeight}px`,
      )

      if (lastAppliedRegion === REGIONS.HEADER) return

      // Code below needs to be done only once for this region
      isAnimated.value = false

      lastAppliedRegion = REGIONS.HEADER
    }

    let scrollHandler = () => {
      const scrollingUp = window.scrollY < lastScrollPos
      lastScrollPos = window.scrollY

      if (lastScrollPos === 0) {
        scrollReset()
        return
      }

      if (lastScrollPos > navHeight) {
        // Scroll up value only has to change in region body
        isScrollUp.value = scrollingUp

        scrollInBody()
        return
      }

      // lastScrollPos <= navHeight
      scrollInHeader()
    }
    window.addEventListener('scroll', scrollHandler, {
      capture: true,
      passive: true,
    })
    onBeforeUnmount(() =>
      window.removeEventListener('scroll', scrollHandler, {
        capture: true,
        passive: true,
      }),
    )
    return {
      generalLinks,
      specialLinks,
      mainLinks,
      root,
      isSticky,
      isAnimated,
      isScrollUp,
    }
  },

  computed: {
    isTablet() {
      return !this.$mq['xl']
    },
  },
}
</script>
<style lang="postcss" scoped>
.desktop-navi {
  --dotcom-desktop-navi-scroll-opacity: 1;
  --dotcom-desktop-navi-scroll-scale: 1;
  --dotcom-desktop-navi-scroll-height: var(--navigation-height) -var(--dotcom-desktop-navi-reduced-height);
  --dotcom-desktop-navi-reduced-height: 96;

  width: 100vw;
  height: var(--navigation-height);
  top: 0;

  /**
   * we had to use a z-index over z-index-10, because this one is already in use by elements
   * like the chat head and scroll to top button
   */
  z-index: calc(var(--z-index-overlay) - 1);
  margin: 0 calc(-1 * var(--body-offset-horizontal));
  padding: var(--spacing-4) var(--body-offset-horizontal);
  background-color: var(--c-primary-neutral-3);
}

.desktop-navi--sticky >>> .main-navi__cta {
  opacity: var(--dotcom-desktop-navi-scroll-opacity);
}

.desktop-navi--sticky >>> .desktop-navi__logo img {
  transform: translateY(var(--dotcom-desktop-navi-scroll-height))
    scale(var(--dotcom-desktop-navi-scroll-scale));
  transform-origin: left top;
}

.desktop-navi--sticky {
  position: sticky;
  top: calc(-1 * var(--navigation-height));
  border-bottom: 1px solid var(--c-secondary-neutral-3);
}

.desktop-navi--animated {
  transition: top 340ms cubic-bezier(0, 0, 0.7, 1);
}

.desktop-navi--sticky.desktop-navi--scroll-up {
  top: calc(-1 * var(--dotcom-desktop-navi-scroll-height));
}

.desktop-navi__upper,
.desktop-navi__lower {
  display: flex;
}

.desktop-navi__upper {
  justify-content: space-between;
  align-items: flex-end;
  gap: 0 var(--spacing-6);
}

.desktop-navi__lower {
  justify-content: flex-end;
  align-items: center;
  gap: 0 var(--spacing-8);
}

.desktop-navi__logo {
  /* each navigation component as a vertically decreasing z-index because of the popovers */
  z-index: var(--z-index-navigation);
}

.desktop-navi__main_navigation {
  z-index: var(--z-index-flyout);
  align-items: flex-end;
}

.desktop-navi__secondary {
  flex: 1;
}

.desktop-navi__secondary_navigation {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}
</style>
